import React from 'react';
import styled from 'styled-components';
import { FaArrowUp, FaHeart } from 'react-icons/fa';
import { personalInfo } from '../data/personal';
import { socialLinks } from '../data/navigation';

const FooterWrapper = styled.footer`
  background: ${({ theme }: { theme: any }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }: { theme: any }) => theme.colors.border};
  padding: ${({ theme }: { theme: any }) => theme.spacing['3xl']} 0 ${({ theme }: { theme: any }) => theme.spacing.xl} 0;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }: { theme: any }) => theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: ${({ theme }: { theme: any }) => theme.spacing.xl};

  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: ${({ theme }: { theme: any }) => theme.spacing.lg};
  }
`;

const FooterInfo = styled.div`
  .footer-name {
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes.lg};
    font-weight: ${({ theme }: { theme: any }) => theme.fontWeights.bold};
    color: ${({ theme }: { theme: any }) => theme.colors.textPrimary};
    margin-bottom: ${({ theme }: { theme: any }) => theme.spacing.xs};
  }

  .footer-role {
    color: ${({ theme }: { theme: any }) => theme.colors.textMuted};
    font-size: ${({ theme }: { theme: any }) => theme.fontSizes.sm};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }: { theme: any }) => theme.spacing.md};
  justify-content: center;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ theme }: { theme: any }) => theme.colors.background};
  border: 1px solid ${({ theme }: { theme: any }) => theme.colors.border};
  border-radius: ${({ theme }: { theme: any }) => theme.borderRadius.full};
  color: ${({ theme }: { theme: any }) => theme.colors.textMuted};
  font-size: ${({ theme }: { theme: any }) => theme.fontSizes.lg};
  transition: all ${({ theme }: { theme: any }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }: { theme: any }) => theme.colors.navyBlue};
    border-color: ${({ theme }: { theme: any }) => theme.colors.navyBlue};
    color: ${({ theme }: { theme: any }) => theme.colors.textPrimary};
    transform: translateY(-2px);
  }

  &.github:hover {
    background: ${({ theme }: { theme: any }) => theme.colors.textPrimary};
    color: ${({ theme }: { theme: any }) => theme.colors.background};
  }

  &.linkedin:hover {
    background: #0077b5;
    border-color: #0077b5;
  }
`;

const Copyright = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }: { theme: any }) => theme.spacing.xs};
  color: ${({ theme }: { theme: any }) => theme.colors.textMuted};
  font-size: ${({ theme }: { theme: any }) => theme.fontSizes.sm};

  .heart {
    color: ${({ theme }: { theme: any }) => theme.colors.navyBlue};
    animation: heartbeat 2s ease-in-out infinite;
  }

  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }

  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const BackToTop = styled.button`
  position: fixed;
  bottom: ${({ theme }: { theme: any }) => theme.spacing.xl};
  right: ${({ theme }: { theme: any }) => theme.spacing.xl};
  width: 50px;
  height: 50px;
  background: ${({ theme }: { theme: any }) => theme.colors.navyBlue};
  border: none;
  border-radius: ${({ theme }: { theme: any }) => theme.borderRadius.full};
  color: ${({ theme }: { theme: any }) => theme.colors.textPrimary};
  font-size: ${({ theme }: { theme: any }) => theme.fontSizes.lg};
  cursor: pointer;
  transition: all ${({ theme }: { theme: any }) => theme.transitions.normal};
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
  z-index: 100;

  &:hover {
    background: ${({ theme }: { theme: any }) => theme.colors.lightNavy};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(30, 58, 138, 0.4);
  }

  @media (max-width: ${({ theme }: { theme: any }) => theme.breakpoints.md}) {
    bottom: ${({ theme }: { theme: any }) => theme.spacing.lg};
    right: ${({ theme }: { theme: any }) => theme.spacing.lg};
    width: 45px;
    height: 45px;
  }
`;

export const FooterSection: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <FooterWrapper>
        <FooterContainer>
          <FooterInfo>
            <div className="footer-name">{personalInfo.name}</div>
            <div className="footer-role">{personalInfo.title}</div>
          </FooterInfo>

          <SocialLinks>
            {socialLinks.map((link) => (
              <SocialLink
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={link.name.toLowerCase()}
                title={link.name}
              >
                {React.createElement(link.icon as any)}
              </SocialLink>
            ))}
          </SocialLinks>

          <Copyright>
            Made with {React.createElement(FaHeart as any, { className: "heart" })} in Kerala
          </Copyright>
        </FooterContainer>
      </FooterWrapper>

      <BackToTop onClick={scrollToTop} title="Back to top">
        {React.createElement(FaArrowUp as any)}
      </BackToTop>
    </>
  );
}; 